import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Slider, Tooltip } from '@mui/material';
import classNames from 'classnames';
import css from './TimelineZoomLevel.module.scss';

type TimelineZoomLevelProps = {
  style?: React.CSSProperties;
  onChange: (value: number) => void;
  onChangeCommitted?: (value: number) => void;
  onCenterOnTimestamp?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  value: number;
  minZoom: number;
  maxZoom: number;
  compact?: boolean;
};

export const TimelineZoomLevel = ({
  style,
  onChange,
  onChangeCommitted,
  onCenterOnTimestamp,
  onDragStart,
  onDragEnd,
  value,
  minZoom,
  maxZoom,
  compact,
}: TimelineZoomLevelProps) => {
  const minLog = Math.log10(minZoom);
  const maxLog = Math.log10(maxZoom);

  // Convert zoom value to slider position (0-100)
  const zoomToSlider = (zoom: number) => {
    const logZoom = Math.log10(zoom);
    return ((logZoom - minLog) / (maxLog - minLog)) * 100;
  };

  // Convert slider position to zoom value
  const sliderToZoom = (sliderValue: number) => {
    const logZoom = minLog + (sliderValue / 100) * (maxLog - minLog);
    return Math.round(Math.pow(10, logZoom));
  };

  const sliderValue = zoomToSlider(value);

  return (
    <div className={classNames(css.sliderContainer, compact && css.compact)} style={style}>
      <div className={css.sliderLabel}>
        <span>Timeline zoom level</span>
        {!compact && (
          <Tooltip
            title="Drag left to narrow the view of clips. Drag right to zoom in and see clips in more detail."
            arrow
            placement="top"
          >
            <InfoRoundedIcon color="primary" />
          </Tooltip>
        )}
      </div>
      <div className={css.slider}>
        <Slider
          min={0}
          max={100}
          step={1}
          value={sliderValue}
          onChange={(_, value) => {
            onChange(sliderToZoom(value as number));
            onCenterOnTimestamp?.();
          }}
          onChangeCommitted={(_, value) => {
            onChangeCommitted?.(sliderToZoom(value as number));
            onCenterOnTimestamp?.();
            onDragEnd?.();
          }}
          onMouseDown={() => onDragStart?.()}
          onTouchStart={() => onDragStart?.()}
        />
        {!compact && <span className={css.sliderValue}>{value}%</span>}
      </div>
    </div>
  );
};
